<template>
  <div
    :style="{ 'z-index': getPopupZindex }"
    :class="{ 'customer-form-popup': true, 'is-visible': isVisible }">
    <h2>{{ popupTitle }}</h2>

    <field type="full-width">
      <label slot="label">
        {{ $t('customerFormPopup.name') }}
      </label>
      <input
        :class="{ 'is-invalid': errors.indexOf('empty-name') > -1 }"
        type="text"
        v-model="name"
        slot="field"
        :disabled="loadingData"
        @keyup="clearErrors('empty-name')" />
      <small
        slot="note"
        class="note"
        v-if="errors.indexOf('empty-name') > -1">
        {{ $t('customerFormPopup.nameIsRequired') }}
      </small>
    </field>

    <field type="full-width">
      <label slot="label">
        {{ $t('customerFormPopup.email') }}
      </label>
      <input
        :class="{ 'is-invalid': errors.indexOf('wrong-email') > -1 || errors.indexOf('used-email') > -1 || errors.indexOf('empty-email') > -1 }"
        type="email"
        v-model="email"
        :disabled="loadingData"
        slot="field"
        @keyup="clearErrors('wrong-email,used-email,empty-email')" />
      <small
        slot="note"
        class="note"
        v-if="errors.indexOf('wrong-email') > -1">
        {{ $t('customerFormPopup.invalidEmail') }}
      </small>
      <small
        slot="note"
        class="note"
        v-if="errors.indexOf('empty-email') > -1">
        {{ $t('customerFormPopup.emailIsRequired') }}
      </small>
      <small
        slot="note"
        class="note"
        v-if="errors.indexOf('used-email') > -1">
        {{ $t('customerFormPopup.emailIsInUse') }}
      </small>
    </field>

    <field type="full-width">
      <label slot="label">
        {{ $t('customerFormPopup.mobilePhone') }}
      </label>
      <div
        class="field-phone"
        slot="field">
        <vue-tel-input
          v-if="phoneFieldVisible"
          :disabled="loadingData"
          :defaultCountry="phonePrefix"
          autocomplete="off"
          :disabledFetchingCountry="true"
          :dynamicPlaceholder="false"
          :placeholder="''"
          :inputClasses="{ 'is-invalid': errors.indexOf('empty-phone') > -1 || errors.indexOf('wrong-phone') > -1 || errors.indexOf('used-phone') > -1 }"
          :required="this.$store.state.config.company.customers_based_on === 'phone' || !!this.$store.state.config.company.require_phone"
          :validCharactersOnly="true"
          v-model="phoneField"
          @input="updatePhone" />
      </div>
      <small
        slot="note"
        class="note"
        v-if="errors.indexOf('empty-phone') > -1">
        {{ $t('customerFormPopup.mobilePhoneIsRequired') }}
      </small>
      <small
        slot="note"
        class="note"
        v-if="errors.indexOf('wrong-phone') > -1">
        {{ $t('customerFormPopup.mobilePhoneIsInvalid') }}
      </small>
      <small
        slot="note"
        class="note"
        v-if="errors.indexOf('used-phone') > -1">
        {{ $t('customerFormPopup.mobilePhoneIsInUse') }}
      </small>
    </field>

    <btn
      @click.native="addCustomer"
      type="popup-left primary">
      {{ saveButtonLabel }}
    </btn>

    <btn
      @click.native="cancel"
      type="popup-right">
      {{ $t('common.ui.cancel') }}
    </btn>
  </div>
</template>

<script>
import ErrorUtils from './../helpers/ErrorUtils.js';
import PopupZindex from './../mixins/PopupZindex';
import { VueTelInput } from 'vue-tel-input';
import Vue from 'vue';

export default {
  name: 'customer-form-popup',
  mixins: [
    PopupZindex
  ],
  components: {
    'vue-tel-input': VueTelInput
  },
  data () {
    return {
      id: 0,
      name: '',
      phone: '',
      phoneField: '',
      email: '',
      errors: [],
      isVisible: false,
      loadingData: false,
      phoneFieldVisible: true
    };
  },
  computed: {
    phonePrefix () {
      return this.$store.state.config.company.phone_prefix;
    },
    popupTitle () {
      if (this.id === 0) {
        return this.$t('customerFormPopup.titleAdd');
      }

      return this.$t('customerFormPopup.titleEdit');
    },
    saveButtonLabel () {
      if (this.id === 0) {
        return this.$t('customerFormPopup.addClient');
      }

      return this.$t('common.ui.saveChanges');
    }
  },
  watch: {
    // Required to get proper country flag in the input
    phonePrefix (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.phoneFieldVisible = false;

        Vue.nextTick(() => {
          this.phoneFieldVisible = true;
        });
      }
    }
  },
  mounted () {
    this.$bus.$on('show-customer-form', this.show);
  },
  methods: {
    show (data) {
      if (!data) {
        this.id = 0;
        this.name = '';
        this.phone = '';
        this.phoneField = '';
        this.email = '';
        this.errors = [];
        this.loadingData = false;
        this.isVisible = true;
        return;
      }

      this.id = data.id;
      this.name = data.name;
      this.phone = data.phone;
      this.phoneField = data.phone;
      this.email = data.email;
      this.errors = [];
      this.loadingData = false;
      this.isVisible = true;
      this.increasePopupZindex();
    },
    hide () {
      this.name = '';
      this.phone = '';
      this.email = '';
      this.isVisible = false;
      this.removePopupZindex();
    },
    addCustomer () {
      this.loadingData = true;

      this.validate();

      if (this.errors.length) {
        this.loadingData = false;
        return;
      }

      let userData = {
        name: this.name,
        phone: this.phone,
        email: this.email
      };

      if (this.id !== 0) {
        userData.id = this.id;
      }

      this.$http.post('/panel/customers/save-customer', userData).then(response => {
        if (response.data.success === 0) {
          if (this.$store.state.config.company.customers_based_on === 'phone') {
            this.errors.push('used-phone');
          } else if (this.$store.state.config.company.customers_based_on === 'email') {
            this.errors.push('used-email');
          }
        } else {
          let searchPhrase = this.name;

          if (this.id !== 0) {
            searchPhrase = false;

            setTimeout(() => {
              this.$bus.$emit('customer-reload-needed');
            }, 1000);
          }

          this.$bus.$emit('customers-reload-needed', searchPhrase);
          this.hide();
        }

        this.loadingData = false;
      }).catch(error => {
        ErrorUtils.handleSubmitError(error, '/panel/customers/save-customer', this.$router, this.$bus, this.addCustomer.bind(this));
        this.loadingData = false;
      });
    },
    validate (response) {
      if (this.name.trim() === '') {
        this.errors.push('empty-name');
      }

      if (
        (this.$store.state.config.company.customers_based_on === 'phone' || !!this.$store.state.config.company.require_phone) &&
        (!this.phone || this.phone.trim() === '')
      ) {
        this.errors.push('empty-phone');
      }

      if (
        (
          this.$store.state.config.company.customers_based_on === 'email' ||
          !!this.$store.state.config.company.require_mail
        ) &&
        this.email.trim() === ''
      ) {
        this.errors.push('empty-email');
      }

      if (this.email !== '' && !(this.email.indexOf('@') > -1 && this.email.indexOf('.') > -1 && this.email.indexOf(' ') === -1)) {
        this.errors.push('wrong-email');
      }
    },
    clearErrors (errorsToClear) {
      errorsToClear = errorsToClear.split(',');

      for (let i = 0; i < errorsToClear.length; i++) {
        let pos = this.errors.indexOf(errorsToClear[i]);
        this.errors.splice(pos, 1);
      }
    },
    cancel () {
      this.hide();
    },
    updatePhone (newPhone, newPhoneData) {
      if (newPhoneData.number.international) {
        this.phone = newPhoneData.number.international;
      } else {
        this.phone = '';
      }
      this.clearErrors('empty-phone,used-phone,wrong-phone');
    }
  },
  beforeDestroy () {
    this.$bus.$off('show-customer-form', this.show);
  }
};
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables.scss';

.customer-form-popup {
  background: $color-light;
  border-radius: $border-radius-small;
  box-shadow: 0 1rem 2rem rgba(0, 0, 0, .125);
  display: none;
  left: 50%;
  max-width: 94%;
  padding: 3rem 4rem 8rem 4rem;
  position: fixed;
  top: 50%;
  transform: translateX(-50%) translateY(-50%);
  width: 480px;

  &.is-visible {
    display: block;
  }

  h2 {
    font-weight: 400;
    line-height: 1;
    margin: 0 0 2rem 0;
  }

  .button-popup-left {
    border-radius: 0 0 0 $border-radius-small;
  }

  .button-popup-right {
    border-radius: 0 0 $border-radius-small 0;
  }

  .field-phone {
    position: relative;
    width: 66.666666%;

    & + .note {
      color: $color-danger;
    }
  }
}

@media (max-width: 480px) {
  .customer-form-popup {
    border-radius: 0;
    height: 100%;
    max-width: 100%;
    padding: 1.5rem 1.5rem 5rem 1.5rem;
    width: 100%;
    z-index: 100;

    .field {
      padding: 0 0 1rem 0;

      & > * {
        width: 100%!important;
      }

      label {
        padding: 0 0 .5rem 0;
        text-align: left;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/ui/CustomerFormPopup.vue